import BaseService from "services/base";
import { get } from "utils/lodash";
import { io, Socket } from "socket.io-client";

// Import GraphQL queries and mutations directly
import {
  GET_CHAT_ROOMS_QUERY,
  GET_CHAT_ROOM_QUERY,
  GET_CHAT_MESSAGES_QUERY
} from 'graphql/queries/chat';
import {
  INITIALIZE_OR_GET_CHAT_ROOM_MUTATION,
  SEND_MESSAGE_MUTATION,
  MARK_AS_READ_MUTATION
} from 'graphql/mutations/chat';
import {
  CHAT_ROOM_FIELDS_FRAGMENT,
  MESSAGE_WITH_SENDER_FRAGMENT,
  MESSAGE_MUTATION_RESPONSE_FRAGMENT
} from 'graphql/fragments/chat';

import { socketRoomManager } from './SocketRoomManager';

export interface Message {
  id: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
  status?: "sent" | "delivered" | "read" | "sending" | "failed";
  sender?: {
    id: string;
    name: string;
    avatar?: string | null;
  };
  chatRoom?: string | null;
}

export interface MessageResponse {
  data: Message[];
  params: {
    total: number;
    totalPage: number;
    limit: number;
    page: number;
    offset: number;
    totalDocs?: number;
    totalPages?: number;
    hasPrevPage?: boolean;
    hasNextPage?: boolean;
  };
}

export interface RoomParams {
  limit?: number;
  page?: number;
  offset?: number;
  filter?: Record<string, any>;
  sort?: Record<string, any>;
  search?: Record<string, any>;
  where?: Record<string, any>;
}

export interface MessageParams {
  roomId: string;
  limit?: number;
  page?: number;
  offset?: number;
  before?: string; // Optional parameter for pagination
}

export interface SendMessageParams {
  roomId: string;
  content: string;
}

class ChatService extends BaseService {
  private socket: Socket | null = null;
  private messageBuffer: Array<{ event: string; data: any }> = [];
  private isReconnecting: boolean = false;
  private serviceListenersAttached: boolean = false;

  // Core socket connection management
  initializeSocket(token: string): Socket {
    if (this.socket?.connected) {
      console.log('♻️ Returning existing connected socket, ID:', this.socket.id);
      return this.socket;
    }

    if (this.socket && !this.socket.connected) {
      console.log('🔄 Reconnecting existing socket...');
      this.socket.connect();
      return this.socket;
    }

    console.log('🆕 Creating new socket connection...');

    const wsUrl = process.env.NEXT_PUBLIC_WS_URL || 'https://cms-v2.lookme.vn';
    const wsPath = process.env.NEXT_PUBLIC_WS_PATH || '/socket.io';
    const transports = process.env.NEXT_PUBLIC_WS_TRANSPORTS?.split(',') || ['websocket'];

    this.socket = io(wsUrl, {
      path: wsPath,
      auth: { token },
      transports: transports as any,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      upgrade: true,
      rememberUpgrade: true,
      withCredentials: true,
      forceNew: false,
      multiplex: true,
    });

    // IMPORTANT: Set socket in room manager BEFORE attaching listeners
    // This ensures the room manager can respond to the initial 'connect' event
    socketRoomManager.setSocket(this.socket);

    if (!this.serviceListenersAttached) {
      this.attachConnectionListeners(token);
      this.serviceListenersAttached = true;
    }

    return this.socket;
  }

  private attachConnectionListeners(token: string) {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('🟢 Socket connected to server');
      
      // Flush buffered messages after reconnection
      if (this.isReconnecting && this.messageBuffer.length > 0) {
        this.messageBuffer.forEach(({ event, data }) => {
          this.socket?.emit(event, data);
        });
        this.messageBuffer = [];
      }
      this.isReconnecting = false;
    });

    // Log all events for debugging
    this.socket.onAnyOutgoing((eventName, ...args) => {
      console.log('📤 Socket event sent:', eventName, args);
    });

    this.socket.on('connect_error', (error) => {
      console.error('🔴 Socket connection error:', error);
      
      // Handle session errors by recreating socket
      if (error.message?.includes('Session ID unknown') || 
          error.message?.includes('400')) {
        this.serviceListenersAttached = false;
        
        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket.disconnect();
          this.socket = null;
        }
        
        setTimeout(() => {
          this.initializeSocket(token);
        }, 1000);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔴 Socket disconnected:', reason);
      this.isReconnecting = true;
      
      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });

    this.socket.on('reconnect', () => {
      console.log('🔄 Socket reconnected');
      this.isReconnecting = false;
    });
  }

  // Simple emit with buffering
  emit(event: string, data: any) {
    if (!this.socket) {
      console.warn('📵 Socket not initialized, cannot emit event:', event);
      return;
    }

    if (this.socket.connected) {
      this.socket.emit(event, data);
    } else {
      this.messageBuffer.push({ event, data });
      console.log('📦 Buffered event:', event);
    }
  }

  // Connection status
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Get socket instance for direct use
  getSocket(): Socket | null {
    return this.socket;
  }

  // Clean disconnect
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.serviceListenersAttached = false;
    }
  }

  // Legacy methods - simplified implementations
  async getRooms(
    shopId: string,
    page: number = 1,
    limit: number = 20,
    searchQuery?: string,
  ) {
    // Note: shopId and searchQuery are not supported by the standard ChatRooms query
    // Using where clause for filtering if shopId is provided
    const whereClause = shopId ? {
      shop: { equals: shopId }
    } : undefined;
    
    return this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: { DATA: "data.ChatRooms.docs", PARAMS: "data.ChatRooms", ERRORS: "errors" },
        variables: { page, limit, where: whereClause },
      },
      GET_CHAT_ROOMS_QUERY
    );
  }

  // Alias for backward compatibility
  async getChatRooms(
    shopIdOrParams: string | { 
      shopId?: string; 
      page?: number; 
      limit?: number; 
      searchQuery?: string;
      where?: any;
    },
    page: number = 1,
    limit: number = 20,
    searchQuery?: string,
  ) {
    let shopId: string;
    let actualPage: number;
    let actualLimit: number;
    let actualSearchQuery: string | undefined;

    if (typeof shopIdOrParams === 'string') {
      // Legacy individual parameters
      shopId = shopIdOrParams;
      actualPage = page;
      actualLimit = limit;
      actualSearchQuery = searchQuery;
    } else {
      // New object parameter
      shopId = shopIdOrParams.shopId || shopIdOrParams.where?.shop?.equals || '';
      actualPage = shopIdOrParams.page ?? 1;
      actualLimit = shopIdOrParams.limit ?? 20;
      actualSearchQuery = shopIdOrParams.searchQuery;
    }

    return this.getRooms(shopId, actualPage, actualLimit, actualSearchQuery);
  }

  async getChatRoomById(roomId: string) {
    return this.getRoom(roomId);
  }

  async getRoom(roomId: string) {
    return this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: { DATA: "data.ChatRoom", PARAMS: "", ERRORS: "errors" },
        variables: { roomId },
      },
      GET_CHAT_ROOM_QUERY
    );
  }

  async initializeOrGetChatRoom(customerId: string, shopId: string) {
    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: { DATA: "data.initializeOrGetChatRoom", PARAMS: "", ERRORS: "errors" },
        variables: { userId: customerId, shopId },
      },
      INITIALIZE_OR_GET_CHAT_ROOM_MUTATION
    );
  }

  async getMessages(
    roomIdOrParams: string | { roomId: string; page?: number; limit?: number; offset?: number },
    page: number = 1,
    limit: number = 20
  ) {
    let roomId: string;
    let actualPage: number;
    let actualLimit: number;

    if (typeof roomIdOrParams === 'string') {
      // Legacy individual parameters
      roomId = roomIdOrParams;
      actualPage = page;
      actualLimit = limit;
    } else {
      // New object parameter
      roomId = roomIdOrParams.roomId;
      actualPage = roomIdOrParams.page ?? 1;
      actualLimit = roomIdOrParams.limit ?? 20;
      // Handle offset if provided (convert to page)
      if (roomIdOrParams.offset !== undefined) {
        actualPage = Math.floor(roomIdOrParams.offset / actualLimit) + 1;
      }
    }

    return this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: { DATA: "data.getChatMessages.docs", PARAMS: "data.getChatMessages", ERRORS: "errors" },
        variables: { chatRoomId: roomId, page: actualPage, limit: actualLimit },
      },
      GET_CHAT_MESSAGES_QUERY
    );
  }

  async sendMessage(
    roomIdOrParams: string | { roomId: string; content: string; attachments?: any[] },
    content?: string,
    attachments?: any[]
  ) {
    let roomId: string;
    let messageContent: string;
    let messageAttachments: any[] | undefined;

    if (typeof roomIdOrParams === 'string') {
      // Legacy individual parameters
      roomId = roomIdOrParams;
      messageContent = content!;
      messageAttachments = attachments;
    } else {
      // New object parameter
      roomId = roomIdOrParams.roomId;
      messageContent = roomIdOrParams.content;
      messageAttachments = roomIdOrParams.attachments;
    }

    // Get socket ID if available
    const socketId = this.socket?.id || undefined;
    console.log('📨 Sending message with socketId:', socketId);

    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: { DATA: "data.sendMessage", PARAMS: "", ERRORS: "errors" },
        variables: {
          chatRoomId: roomId,
          content: messageContent,
          attachmentIds: messageAttachments,
          socketId: socketId
        },
      },
      SEND_MESSAGE_MUTATION
    );
  }

  // Storage methods
  private storeRooms(data: any) {
    try {
      localStorage.setItem('chat_rooms', JSON.stringify(data));
    } catch (error) {
      console.error('Failed to store rooms:', error);
    }
  }

  getStoredRooms() {
    try {
      const stored = localStorage.getItem('chat_rooms');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private storeMessages(roomId: string, data: any) {
    try {
      const key = `chat_messages_${roomId}`;
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to store messages:', error);
    }
  }

  getStoredMessages(roomId: string) {
    try {
      const key = `chat_messages_${roomId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  clearStoredMessages(roomId?: string) {
    try {
      if (roomId) {
        localStorage.removeItem(`chat_messages_${roomId}`);
      } else {
        Object.keys(localStorage)
          .filter(key => key.startsWith('chat_messages_'))
          .forEach(key => localStorage.removeItem(key));
      }
    } catch (error) {
      console.error('Failed to clear stored messages:', error);
    }
  }
}

const Instance = new ChatService();
export default Instance;